import { useNavigate } from 'react-router-dom'
import { Check, LogOut, Palette, Settings, Sun, User as UserIcon } from 'lucide-react'

import type { Theme } from '@/lib/theme/theme-storage'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/lib/auth/useAuth'
import { THEME_META, THEMES } from '@/lib/theme/theme-storage'
import { useTheme } from '@/lib/theme/useTheme'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Paletas autorais Vectra V1..V6 (`theme-v1`..`theme-v6`) são showcase
 * histórico — continuam disponíveis na galeria `/themes` mas saem do menu
 * do avatar pra não poluir o dropdown do user (pedido Marcelo 2026-05-16).
 *
 * `vectra-v3` (default oficial) e os themes terceiros (Solarized / Tokyo
 * Night / VS Code) continuam visíveis.
 */
const AVATAR_HIDDEN_THEMES: ReadonlySet<Theme> = new Set([
  'theme-v1',
  'theme-v2',
  'theme-v4',
  'theme-v5',
  'theme-v6',
])

export function UserMenu() {
  const { user, logout } = useAuth()
  const [theme, setTheme] = useTheme()
  const navigate = useNavigate()

  // Defesa: o UserMenu só renderiza dentro de rotas protegidas (RequireAuth
  // garante user != null). Mas durante a janela rapidíssima entre login e
  // navigate, o React pode chamar render uma vez ainda sem user.
  if (!user) return null

  async function handleLogout() {
    try {
      await logout()
      toast.success('Sessão encerrada.')
    } catch {
      // logout() já é best-effort; nunca rejeita. Mas TS não sabe disso.
      toast.error('Não foi possível sair limpo. Sessão local foi limpa mesmo assim.')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-9 gap-2 px-2"
          aria-label="Menu do usuário"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={user.avatarUrl ?? undefined} alt={user.name} />
            <AvatarFallback>{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span className="hidden text-sm font-medium md:inline">
            {user.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span>{user.name}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {user.email}
          </span>
          <span className="mt-0.5 text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
            {user.role}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Profile e Preferences apontam pras tabs reais de /settings/user (já
            existem desde VEC-140). Antes mostravam toast.info "em breve" — hits
            B1 e B2 da auditoria de botões fantasmas. Opção B aprovada
            (docs/AUDITORIA-BOTOES-FANTASMAS.md). */}
        <DropdownMenuItem onSelect={() => navigate('/settings/user?tab=profile')}>
          <UserIcon className="mr-2 h-4 w-4" /> Perfil
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings/user?tab=preferences')}>
          <Settings className="mr-2 h-4 w-4" /> Configurações
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <Palette className="h-3 w-3" /> Aparência
        </DropdownMenuLabel>
        {THEMES.filter((id) => !AVATAR_HIDDEN_THEMES.has(id)).map((id) => {
          const meta = THEME_META[id]
          const active = theme === id
          return (
            <DropdownMenuItem
              key={id}
              onSelect={(event) => {
                // Evita o fechamento imediato do menu quando o usuário só
                // quer trocar o tema sem perder o contexto do submenu.
                event.preventDefault()
                setTheme(id)
                toast.success(`Tema aplicado: ${meta.label}`, {
                  description: meta.description,
                  duration: 2200,
                })
              }}
              aria-checked={active}
              role="menuitemradio"
            >
              {meta.scheme === 'light' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Palette className="mr-2 h-4 w-4" />
              )}
              <span className="flex-1">{meta.label}</span>
              {active ? <Check className="ml-2 h-4 w-4 text-primary" /> : null}
            </DropdownMenuItem>
          )
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void handleLogout()}>
          <LogOut className="mr-2 h-4 w-4" /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
